import React, { useState } from 'react';
import { useTranslations } from 'react-ui';
import { FormFooter } from '../common/FormFooter';

interface AiTextEntryProps {
  isExtracting: boolean;
  extractionError: string | null;
  onExtract: (text: string) => void;
  onClearError: () => void;
  onCancel: () => void;
}

const MAX_CHARS = 5000;
const MIN_CHARS = 20;

/**
 * AI text-entry panel: textarea + generate button.
 * Used in the 'ai-text' entry mode of QuickCreateModal.
 */
export const AiTextEntry: React.FC<AiTextEntryProps> = ({
  isExtracting,
  extractionError,
  onExtract,
  onClearError,
  onCancel,
}) => {
  const { translated } = useTranslations();
  const [text, setText] = useState('');

  const charCount = text.length;
  const canSubmit = charCount >= MIN_CHARS && charCount <= MAX_CHARS && !isExtracting;

  const handleSubmit = () => {
    if (canSubmit) {
      onExtract(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) {
      handleSubmit();
    }
  };

  return (
    <div className="w-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col gap-4 pb-2 px-px">
      <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-medium text-amber-700">
        <span>🧪</span>
        <span>{translated('sc_ai_beta_note')}</span>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="ai-text-input" className="text-sm font-medium text-gray-700">
          {translated('sc_ai_text_label')}
        </label>
        <textarea
          id="ai-text-input"
          value={text}
          onChange={e => {
            setText(e.target.value);
            if (extractionError) onClearError();
          }}
          onKeyDown={handleKeyDown}
          placeholder={translated('sc_ai_text_placeholder')}
          maxLength={MAX_CHARS}
          rows={10}
          disabled={isExtracting}
          className="w-full resize-none rounded-lg border border-gray-300 p-3 text-sm text-gray-900 placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-balloon focus:border-balloon
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-balloon focus-visible:border-balloon
            disabled:bg-gray-50 disabled:text-gray-400 transition-colors"
          aria-describedby="ai-text-char-count"
        />
        <div id="ai-text-char-count" className="flex justify-between text-xs text-gray-500">
          <span>
            {charCount < MIN_CHARS && charCount > 0
              ? translated('sc_ai_text_min_chars').replace('{min}', String(MIN_CHARS))
              : translated('sc_ai_text_hint')}
          </span>
          <span className={charCount > MAX_CHARS * 0.9 ? 'text-orange-500' : ''}>
            {charCount} / {MAX_CHARS}
          </span>
        </div>
      </div>

      {extractionError && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800"
        >
          <span className="shrink-0 mt-0.5">❌</span>
          <span className="flex-1">{extractionError}</span>
          <button
            type="button"
            onClick={onClearError}
            className="shrink-0 p-0.5 hover:opacity-70 transition-opacity"
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}
      </div>

      <FormFooter
        onCancel={onCancel}
        onContinue={handleSubmit}
        cancelLabel={translated('cancel')}
        continueLabel={`${translated('sc_ai_text_generate')} →`}
        cancelDisabled={isExtracting}
        continueDisabled={!canSubmit}
        isLoading={isExtracting}
        loadingLabel={translated('sc_ai_text_generating')}
      />
    </div>
  );
};
